import { memo, useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { getLocationFor, pluralize } from '@trinity/chat-components/utils';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { useRenameFolderModal } from '../Workspace.Folder/hooks/useRenameFolderModal';
import { ActiveChatSessionContext } from './context';
import { FileUploadFileContainer } from './FileUpload.File.Container';
import { Link } from '../presentation/Link';

type Props = {
  className?: string;
  item: Chat.InstanceFileUploadsItem;
  folder: {
    id: number;
    name: string;
    workspaceId: number;
  };
  onRenameSuccess?: (folderName: string) => void;
};

const FileUpload = memo(({ className, folder, item, onRenameSuccess }: Props) => {

  const folderLocation = getLocationFor.workspaces.folder({
    folderId: folder.id,
  });

  const [toggleRenameModal, RenameFolderModal] = useRenameFolderModal();

  return (
    <>
      <div className={className}>
        <div>
          <div>
            Uploading {item.items.length} {pluralize('file', item.items.length)} to the <Link to={folderLocation}>{folder.name}</Link> folder.
          </div>

          <EditName onClick={toggleRenameModal}>
            Edit Name
          </EditName>

          Files must finish being uploaded and processed before being available in the chat. This may take a few minutes.
        </div>

        <UploadList>
          {item.items.map(file => (
            <StyledFileUpload
              key={file.identifier}
              uploadIdentifier={item.identifier}
              value={file} />
          ))}
        </UploadList>
      </div>
      <RenameFolderModal
        folderId={folder.id}
        workspaceId={folder.workspaceId}
        name={folder.name}
        onRenameSuccess={onRenameSuccess}
      />
    </>
  );
});

const EditName = styled.div(({ theme }) => ({
  textDecoration: 'underline',
  color: theme.palette.hyperlink,
  cursor: 'pointer',

  margin: '10px 0',
}));

const UploadList = styled.div(({ theme }) => ({
  border: `2px solid ${theme.palette.gray.light2}`,
  borderRadius: 6,

  marginTop: 20,
}));

const StyledFileUpload = styled(FileUploadFileContainer)(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.gray.light2}`,

  '&:last-child': {
    borderBottom: 'none',
  },
}));

type BaseProps = Omit<Props, 'folder'>;

const FileUploadContainer = (props: BaseProps) => {

  const folder = useChatFolder();
  const dispatch = useContext(ChatStateDispatchContext);

  const handleRenameSuccess = useCallback((folderName: string) => {
    dispatch({
      type: 'folder-name-updated',
      payload: {
        value: folderName,
      },
    });
  }, [dispatch]);

  return (
    <FileUpload {...props} folder={folder} onRenameSuccess={handleRenameSuccess} />
  );
};

const useChatFolder = () => {
  const chat = useContext(ActiveChatSessionContext);

  return useMemo(() => {
    return {
      id: chat.context.type === ChatContextType.Folder
        ? chat.context.data.folderId
        : undefined,
      name: chat.context.type === ChatContextType.Folder
        ? chat.context.metadata.name
        : undefined,
      workspaceId: chat.context.type === ChatContextType.Folder
        ? chat.context.data.workspaceId
        : undefined,
    };
  }, [chat.context.type, chat.context.data, chat.context.metadata]);
};

export { FileUploadContainer as FileUpload };